'use client';

/**
 * Tags Section — flat tag pill cloud rendered from multiple string/list fields.
 *
 * @component_role Schema-driven section renderer for SectionType.TAGS
 * @business_rule Takes a list of field names from the schema config, reads each
 *   value from entity data (either a scalar string or a list of strings),
 *   flattens everything into a single deduped tag list, renders as pills.
 *   Used by the DesignIntent details card to show the SemanticProfile
 *   attributes (style, mood, aesthetic, material, colour, setting) as the
 *   keywords that drive the vibe-vector search.
 *
 * @layer presentation
 * @arch_component_type section
 * @arch_domain copilot
 */

import { Hash } from 'lucide-react';
import { SectionHeader } from '../shared';

interface TagsSectionProps {
  fields: string[];
  data: Record<string, unknown>;
  label?: string | null;
}

/** Coerce any value into an array of string tags. Scalar → [scalar], list → list, anything else → []. */
function toTagList(value: unknown): string[] {
  if (value == null) return [];
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  if (Array.isArray(value)) {
    return value
      .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
      .map((v) => v.trim());
  }
  return [];
}

export function TagsSection({ fields, data, label }: TagsSectionProps) {
  // Flatten all declared fields into one tag list, dedupe while preserving order
  const seen = new Set<string>();
  const tags: string[] = [];
  for (const field of fields) {
    for (const tag of toTagList(data[field])) {
      const key = tag.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        tags.push(tag);
      }
    }
  }

  if (tags.length === 0) return null;

  return (
    <div>
      <SectionHeader title={label || 'Keywords'} />
      <div className="flex flex-wrap gap-1.5">
        {tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-primary/10 text-primary border border-primary/20"
          >
            <Hash className="w-2.5 h-2.5 opacity-60" />
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}
